import { useAsyncEffect } from '@hooks/useAsyncEffect';
import { handleError } from '@services/errHandler';
import type { OfflineModule } from '@services/offline/offline.service';
import { offlineService } from '@services/offline/offline.service';
import { PermissionsService } from '@services/permissions';
import i18n from 'i18next';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRouter from './routes/components/AppRouter';
import { useGlobalAuthStore } from './stores/bootstrap-stores/auth';
import { useAppUserStore } from './stores/bootstrap-stores/user';
import { useGlobalUserGroupsStore } from './stores/bootstrap-stores/userGroups';

function normalizeLanguageCode(value: string): 'en' | 'fr' | 'it' {
  const normalized = value.trim().toLowerCase();
  const [base] = normalized.split('-');

  if (base === 'fr' || base === 'it') {
    return base;
  }

  return 'en';
}

export default function App() {
  const userLanguage = useAppUserStore((state) => state.appUser?.system_language);
  const currentMember = useAppUserStore((state) => state.currentMember);
  const user = useAppUserStore((state) => state.appUser);
  const userGroups = useGlobalUserGroupsStore((state) => state.userGroups);
  const isOffline = useGlobalAuthStore((state) => !!state.customClaims.offline);

  useAsyncEffect(
    async (signal) => {
      const nextLanguage = normalizeLanguageCode(userLanguage || 'en');
      if (i18n.language !== nextLanguage) {
        try {
          await i18n.changeLanguage(nextLanguage);
        } catch (error) {
          if (signal.aborted) {
            return;
          }
          handleError(error, 'Failed to change language');
        }
      }

      localStorage.setItem('app_language', nextLanguage);
    },
    [userLanguage]
  );

  useAsyncEffect(
    async (signal) => {
      if (!isOffline || !currentMember || !user) {
        return;
      }

      try {
        if (!user.offline_enabled) {
          return;
        }
        const availableModules: OfflineModule[] = [
          'invitations',
          'responses',
          'workspace',
          'learning-center'
        ];
        const modulesToSync: typeof availableModules = [];

        availableModules.forEach((module) => {
          if (PermissionsService && !PermissionsService.canInvoke(module)) {
            modulesToSync.push(module);
          }
        });

        await offlineService.sync(modulesToSync);
      } catch (error) {
        if (!signal.aborted) {
          handleError(error, 'Could not initialize offline invitations data');
        }
      }
    },
    [currentMember, isOffline, userGroups]
  );

  return (
    <Router>
      <AppRouter />
    </Router>
  );
}
